import Markdown from '@/components/dataDisplay/Markdown';
import CampusesQuestion from '@/modules/chatbot/components/CampusesQuestion';
import CategoryQuestion from '@/modules/chatbot/components/CategoryQuestion';
import PersonaQuestion from '@/modules/chatbot/components/PersonaQuestion';
import ProgramsQuestion from '@/modules/chatbot/components/ProgramsQuestion';
import ProgramStepsQuestion from '@/modules/chatbot/components/ProgramStepsQuestion';
import { LogEvent } from '@/modules/core/amplitude/types/Amplitude';
import areAllQuestionsAnswered from '@/modules/core/chatbot/areAllQuestionsAnswered';
import findStartingQuestion from '@/modules/core/chatbot/findStartingQuestion';
import getQuestionsToAsk, { ChatbotQuestions } from '@/modules/core/chatbot/getQuestionsToAsk';
import { shouldAnimateCurrentQuestion } from '@/modules/core/chatbot/shouldAnimateCurrentQuestion';
import useCustomer from '@/modules/core/data/hooks/useCustomer';
import { hasValue } from '@/modules/core/data/record';
import { AirtableRecord } from '@/modules/core/data/types/AirtableRecord';
import { Campus } from '@/modules/core/data/types/Campus';
import ChatbotQuestion from '@/modules/core/data/types/CDE/ChatbotQuestion';
import UpdatedQuestion from '@/modules/core/data/types/CDE/UpdatedQuestion';
import {
  CHATBOT_CAMPUSES_KEY,
  CHATBOT_CATEGORIES_KEY,
  CHATBOT_EDUCATIONAL_PROGRAM_STEPS_KEY,
  CHATBOT_EDUCATIONAL_PROGRAMS_KEY,
  ChatbotContext,
} from '@/modules/core/data/types/ChatbotContext';
import { Customer } from '@/modules/core/data/types/Customer';
import { CustomerTheme } from '@/modules/core/data/types/CustomerTheme';
import { EducationalProgram } from '@/modules/core/data/types/EducationalProgram';
import { EducationalProgramStep } from '@/modules/core/data/types/EducationalProgramStep';
import { GenericObject } from '@/modules/core/data/types/GenericObject';
import { StudentSolution } from '@/modules/core/data/types/StudentSolution';
import { StudentSolutionsCategory } from '@/modules/core/data/types/StudentSolutionsCategory';
import useUserSession, { UserSession } from '@/modules/core/userSession/useUserSession';
import { flattenStepsFromEducationalPrograms } from '@/modules/solutions/educationalProgramStep';
import {
  filterStudentSolutions,
  getDefaultContext,
} from '@/modules/solutions/solutionsFilters';
import { serializeStudentSolutionContext } from '@/modules/solutions/studentSolutionContext';
import { Amplitude } from '@amplitude/react-amplitude';
import {
  css,
  useTheme,
} from '@emotion/react';
import classnames from 'classnames';
import orderBy from 'lodash.orderby';
import sortBy from 'lodash.sortby';
import React, {
  Fragment,
  useState,
} from 'react';
import {
  Trans,
  useTranslation,
} from 'react-i18next';
import Question from './Question';
import SolutionsFoundCard from './SolutionsFoundCard';

type Props = {
  isInIframe: boolean;
}

/**
 * Displays the whole chatbot dialog.
 *
 * Guides the end-user through questions and choices in order to build a profile "context".
 *
 * XXX This is most likely the worst part of the app and the most complicated.
 *  The chatbot workflow is hardcoded and every edge case is handled manually.
 *  The overall complexity is very high.
 */
const ChatbotDialog: React.FunctionComponent<Props> = (props): JSX.Element => {
  const {
    isInIframe,
  } = props;
  const customerRef = process.env.NEXT_PUBLIC_CUSTOMER_REF;
  const theme: CustomerTheme = useTheme();
  const { t } = useTranslation();
  const customer: Customer = useCustomer();
  const { modChatbot: chatbotSettings } = customer;
  const { primaryColor, onPrimaryColor, primaryColorVariant1, surfaceColor } = theme;
  const bubbleTimerDuration: number = process.env.NEXT_PUBLIC_APP_STAGE === 'development' ? 100 : 1500; // Avoid wasting time in dev mode
  const updatedQuestionAnimationDuration = 200; // Duration of all animations that are performed, when a question's choice is updated
  const allStudentSolutions: AirtableRecord<StudentSolution>[] = customer?.studentSolutions;
  const allEducationalPrograms: AirtableRecord<EducationalProgram>[] = orderBy(customer?.educationalPrograms, ['displayPriorityScore', 'label'], [
    'desc',
    'asc',
  ]);
  const allEducationalProgramSteps: AirtableRecord<EducationalProgramStep>[] = sortBy(flattenStepsFromEducationalPrograms(allEducationalPrograms), 'label');
  const allCampuses: AirtableRecord<Campus>[] = customer?.campuses;
  const allStudentSolutionsCategories: AirtableRecord<StudentSolutionsCategory>[] = orderBy(customer?.studentSolutionsCategories, [
    'displayPriorityScore',
    'label',
  ], [
    'desc',
    'asc',
  ]);
  const userSession: UserSession = useUserSession();
  const [context, setContext] = useState<ChatbotContext>(getDefaultContext({
    customerRef: customerRef,
    persona: userSession?.persona,
  }));
  // console.debug('chatbot context', context);

  const questions: ChatbotQuestions = getQuestionsToAsk(context, t, customer, allEducationalPrograms, allEducationalProgramSteps, allCampuses, allStudentSolutionsCategories, allStudentSolutions);
  const {
    personaQuestion,
  } = questions;
  const [activeQuestion, setActiveQuestion] = useState<ChatbotQuestion>(findStartingQuestion(context, questions, allEducationalPrograms, allEducationalProgramSteps, allCampuses, allStudentSolutionsCategories, chatbotSettings));
  const [updatedQuestion, setUpdatedQuestion] = useState<UpdatedQuestion>(null); // Helps handling animations, when a choice is changed
  const shouldDisplaySolutionsFound: boolean = areAllQuestionsAnswered(context, allEducationalPrograms, allEducationalProgramSteps, allCampuses, allStudentSolutionsCategories, chatbotSettings);
  // console.debug('activeQuestion', activeQuestion);

  const sanitizedEducationalPrograms = context?.[CHATBOT_EDUCATIONAL_PROGRAMS_KEY]?.filter((educationalProgram: EducationalProgram) => !!educationalProgram?.ref?.trim()?.length);
  const sanitizedEducationalProgramSteps = context?.[CHATBOT_EDUCATIONAL_PROGRAM_STEPS_KEY]?.filter((educationalProgramStep: EducationalProgramStep) => !!educationalProgramStep?.ref?.trim()?.length);
  const sanitizedCampuses = context?.[CHATBOT_CAMPUSES_KEY]?.filter((campus: Campus) => !!campus?.ref?.trim()?.length);
  const sanitizedCategories = context?.[CHATBOT_CATEGORIES_KEY]?.filter((category: StudentSolutionsCategory) => !!category?.ref?.trim()?.length);

  // Sanitize context to ignore all choices that should be ignored (such as "Select all" choices, which shouldn't apply any filter)
  const sanitizedContext: ChatbotContext = {
    ...context,
    [CHATBOT_EDUCATIONAL_PROGRAMS_KEY]: sanitizedEducationalPrograms?.length ? sanitizedEducationalPrograms : undefined,
    [CHATBOT_EDUCATIONAL_PROGRAM_STEPS_KEY]: sanitizedEducationalProgramSteps?.length ? sanitizedEducationalProgramSteps : undefined,
    [CHATBOT_CAMPUSES_KEY]: sanitizedCampuses?.length ? sanitizedCampuses : undefined,
    [CHATBOT_CATEGORIES_KEY]: sanitizedCategories?.length ? sanitizedCategories : undefined,
  };
  // Find matching student solutions for the user's context
  const studentSolutionsFilteredByContext: StudentSolution[] = filterStudentSolutions(allStudentSolutions, sanitizedContext);

  return (
    <Amplitude
      eventProperties={(inheritedProps): GenericObject => ({
        ...inheritedProps,
        question: { ref: activeQuestion.ref },
        filters: serializeStudentSolutionContext(sanitizedContext), // Serialize to avoid crash due to circular references (also, we just want identifiable data, not a huge dataset)
      })}
      userProperties={{
        persona: context.persona,
      }}
      // Use debounce so that the nested logEvent() calls will include all context data, otherwise the even will tick before the context is actually updated
      debounceInterval={400}
    >
      {({ logEvent }: { logEvent: LogEvent }): JSX.Element => (
        <div
          css={css`
            width: 100%;

            * {
              clear: both;
            }

            .message-container {
              overflow: scroll;
              -ms-overflow-style: none;
              border-radius: 15px 15px 0px 0px;
              width: 60%;
              margin: auto;
              min-height: 92vh;
              height: auto;
              padding-top: 20px;

              &.solutions-not-found {
                padding-bottom: 300px;
              }
            }

            .message-container::-webkit-scrollbar {
              display: none;
            }

            .message-gradient-scroll {
              position: absolute;
              border-radius: 15px 15px 0px 0px;
              z-index: 1;
              background: rgb(255, 255, 255);
              background: linear-gradient(0deg, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 1) 100%);
              height: 90px;
              width: 60%;
            }

            .message {
              background-color: ${surfaceColor};
              border-radius: 25px 25px 25px 0px;
              padding: 12px 20px 8px 20px;
              margin: 5px;
              max-width: 60%;
              float: left;

              &.user {
                float: right;
                color: ${onPrimaryColor};
                background-color: ${primaryColor};
                border-radius: 25px 25px 0px 25px;

                & b {
                  font-weight: bold;
                }
              }

              i {
                font-style: normal;
                font-weight: lighter;
              }
            }

            .select-input {
              width: 60%;
            }

            .close-message {
              color: grey;
              background-color: white;
              float: right;
              position: relative;
              font-size: 2rem;
              right: -20px;
              top: -25px;
              border-radius: 30px;
            }

            .text-spacing {
              text-align: center;
              margin-top: 200px;
              margin-bottom: 100px;
            }

            .select-group-items-counter {
              float: right;
              font-weight: bold;
            }

            .select-group-label {
              background-color: #D9D9D9;
              padding: 3px 10px 3px 10px;
              margin-top: 15px;
              border-radius: 20px;
            }

            .option-button {
              border: none;
              color: ${primaryColor};
              background-color: ${primaryColorVariant1};
              margin-top: 4px;
              margin-right: 5px;
              border-radius: 30px;
              padding: 12px 20px 8px 20px;
              font-size: 15px;
            }

            @media (max-width: 991.98px) {
              .message-container {
                width: 90%;
              }

              .message {
                max-width: 90%;
              }

              .select-input {
                width: 90%;
              }

              .message-fading {
                width: 90%;
              }
            }
          `}
        >
          <div
            className={classnames('message-container', {
              'solutions-found': shouldDisplaySolutionsFound,
              'solutions-not-found': !shouldDisplaySolutionsFound,
            })}
          >
            <Question
              question={null}
              activeQuestion={activeQuestion}
              context={context}
              // XXX Bypass the "bubbleTimer" when the active question isn't "persona", because that means we're loading the chatbot while already knowing the user's persona
              bubbleTimerDuration={activeQuestion === personaQuestion ? bubbleTimerDuration : 0}
            >
              <Fragment>
                {
                  // Use customer content first, then fallback to default content (Locize)
                  hasValue(chatbotSettings, 'dialogIntroduction') ? (
                    <Markdown
                      className={'message'}
                      text={chatbotSettings?.dialogIntroduction}
                    />
                  ) : (
                    <div className={'message'}>
                      <p>
                        <Trans i18nKey={'chatbotPage.dialog.question.welcome'}>
                          Bienvenue ! Je vais vous aider à solutionner vos études à <strong>{{ label: customer?.label }}</strong>.
                        </Trans>
                      </p>
                    </div>
                  )
                }

                <PersonaQuestion
                  questions={questions}
                  activeQuestion={activeQuestion}
                  context={context}
                  setActiveQuestion={setActiveQuestion}
                  setContext={setContext}
                  updatedQuestion={updatedQuestion}
                  setUpdatedQuestion={setUpdatedQuestion}
                  updatedQuestionAnimationDuration={updatedQuestionAnimationDuration}
                  logEvent={logEvent}
                  bubbleTimerDuration={bubbleTimerDuration}
                  allEducationalPrograms={allEducationalPrograms}
                  allEducationalProgramSteps={allEducationalProgramSteps}
                  allCampuses={allCampuses}
                  allStudentSolutionsCategories={allStudentSolutionsCategories}
                  modChatbot={chatbotSettings}
                />
              </Fragment>
            </Question>

            <ProgramsQuestion
              questions={questions}
              activeQuestion={activeQuestion}
              context={context}
              setActiveQuestion={setActiveQuestion}
              setContext={setContext}
              updatedQuestion={updatedQuestion}
              setUpdatedQuestion={setUpdatedQuestion}
              updatedQuestionAnimationDuration={updatedQuestionAnimationDuration}
              logEvent={logEvent}
              bubbleTimerDuration={bubbleTimerDuration}
              allEducationalPrograms={allEducationalPrograms}
              allEducationalProgramSteps={allEducationalProgramSteps}
              allCampuses={allCampuses}
              allStudentSolutionsCategories={allStudentSolutionsCategories}
              modChatbot={chatbotSettings}
            />

            <ProgramStepsQuestion
              questions={questions}
              activeQuestion={activeQuestion}
              context={context}
              setActiveQuestion={setActiveQuestion}
              setContext={setContext}
              updatedQuestion={updatedQuestion}
              setUpdatedQuestion={setUpdatedQuestion}
              updatedQuestionAnimationDuration={updatedQuestionAnimationDuration}
              logEvent={logEvent}
              bubbleTimerDuration={bubbleTimerDuration}
              allEducationalPrograms={allEducationalPrograms}
              allEducationalProgramSteps={allEducationalProgramSteps}
              allCampuses={allCampuses}
              allStudentSolutionsCategories={allStudentSolutionsCategories}
              modChatbot={chatbotSettings}
            />

            <CampusesQuestion
              questions={questions}
              activeQuestion={activeQuestion}
              context={context}
              setActiveQuestion={setActiveQuestion}
              setContext={setContext}
              updatedQuestion={updatedQuestion}
              setUpdatedQuestion={setUpdatedQuestion}
              updatedQuestionAnimationDuration={updatedQuestionAnimationDuration}
              logEvent={logEvent}
              bubbleTimerDuration={bubbleTimerDuration}
              allEducationalPrograms={allEducationalPrograms}
              allEducationalProgramSteps={allEducationalProgramSteps}
              allCampuses={allCampuses}
              allStudentSolutionsCategories={allStudentSolutionsCategories}
              modChatbot={chatbotSettings}
            />

            <CategoryQuestion
              questions={questions}
              activeQuestion={activeQuestion}
              context={context}
              setActiveQuestion={setActiveQuestion}
              setContext={setContext}
              updatedQuestion={updatedQuestion}
              setUpdatedQuestion={setUpdatedQuestion}
              updatedQuestionAnimationDuration={updatedQuestionAnimationDuration}
              logEvent={logEvent}
              bubbleTimerDuration={bubbleTimerDuration}
              allEducationalPrograms={allEducationalPrograms}
              allEducationalProgramSteps={allEducationalProgramSteps}
              allCampuses={allCampuses}
              allStudentSolutionsCategories={allStudentSolutionsCategories}
              modChatbot={chatbotSettings}
            />

            {
              shouldDisplaySolutionsFound && (
                <SolutionsFoundCard
                  chatbotContext={context}
                  studentSolutionsFilteredByContext={studentSolutionsFilteredByContext}
                  className={classnames({ [`animated fadeIn delay-100ms duration-${updatedQuestionAnimationDuration}ms`]: shouldAnimateCurrentQuestion(updatedQuestion, { order: Infinity }) })}
                  isInIframe={isInIframe}
                />
              )
            }
          </div>
        </div>
      )}
    </Amplitude>
  );
};

export default ChatbotDialog;
